import {
  OEButton,
  OEButtonProps,
  OEText,
  OETextInput,
  useModal,
  ErrorMessageBox,
  SuccessMessage,
  WarnMessage,
  flex,
  height,
  maxWidth,
  mt,
  width,
} from '@openeducation/oe-it-components';
import { useGetProfileConfig } from '@openeducation/oe-it-providers';
import { REGEXP } from '@openeducation/oe-it-services';
import { FREE_FORM_LESSON_UUID } from '@openeducation/oe-it-shared-types';
import { styled } from '@openeducation/oe-it-themes';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Checkbox } from 'react-native-paper';

import { usePcFeedbackForm } from './use-pc-feedback-form';
import { PCFeedbackRubrics } from '../../organisms/pc-feedback-rubrics/pc-feedback-rubrics';
import LessonSelector from '../lesson-selector/lesson-selector';

export interface PCFeedbackFormProps {
  onFeedbackSubmitted: () => void;
}

export const PCFeedbackForm = ({
  onFeedbackSubmitted,
}: PCFeedbackFormProps) => {
  const [teacherFeedbackValue, setTeacherFeedbackValue] = useState('');
  const [recommendRepeat, setRecommendRepeat] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [criteriaPerformanceIds, setCriteriaPerformanceIds] = useState<
    number[]
  >([]);

  const { closeModal, openModal } = useModal();
  const {
    error,
    isLoading,
    lessonId,
    liveClassLessons,
    isLessonSubmitted,
    lessonSelected,
    lessonSubmitted,
    submitFeedback,
    submitLightFeedback,
    isSubmitting,
  } = usePcFeedbackForm();

  const { data: wolverineConfig } = useGetProfileConfig();

  useEffect(() => {
    setTeacherFeedbackValue('');
    setRecommendRepeat(false);
    setCriteriaPerformanceIds([]);
  }, [lessonId]);

  const canSubmit = !!teacherFeedbackValue && !!lessonId && !submittingFeedback;
  const currentLesson = liveClassLessons?.find((l) => l.id === lessonId);
  const isFreeFormTopic =
    !lessonId || lessonId <= 0 || currentLesson?.uuid === FREE_FORM_LESSON_UUID;

  const showRubrics = useMemo(() => {
    return (
      !isFreeFormTopic &&
      lessonId &&
      lessonId > 0 &&
      wolverineConfig?.teacherFeedbackRubricEnabled
    );
  }, [
    isFreeFormTopic,
    lessonId,
    wolverineConfig?.teacherFeedbackRubricEnabled,
  ]);

  const submitFeedbackAction = async () => {
    try {
      setSubmittingFeedback(true);
      await submitFeedback(
        teacherFeedbackValue,
        lessonId as number,
        recommendRepeat,
        criteriaPerformanceIds
      );
      onFeedbackSubmitted();
      openModal(
        <SuccessMessage
          continueLabel="Continue"
          title="Feedback successfully sent!"
          onContinue={closeModal}
        />
      );
    } catch (error) {
      console.error(error);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSubmitFeedback = async () => {
    const lessonMatches =
      isFreeFormTopic &&
      teacherFeedbackValue.match(REGEXP.LESSON_FORMAT_IN_TEXT);
    if (lessonMatches)
      openModal(
        <WarnMessage
          body={`Oops! It looks like you’re about to submit feedback for a freeform class.
          In your comments, you mention lesson **${lessonMatches[0]}**. Are you sure the feedback’s ready to submit?
          Thanks for double-checking!`}
          buttons={[
            {
              label: 'Cancel',
              mode: 'text',
              type: 'dark0',
              onPress: closeModal,
            },
            {
              label: 'Submit anyway',
              onPress: submitFeedbackAction,
            },
          ]}
        />
      );
    else submitFeedbackAction();
    return true;
  };

  const handleSubmitLightFeedback = async (lessonId: number) => {
    try {
      await submitLightFeedback(lessonId);
      if (!error) {
        lessonSubmitted();
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading || !liveClassLessons) return null;

  return (
    <View style={[maxWidth(700), width(700)]}>
      <LessonSelector
        lessonId={lessonId}
        isLessonSubmitted={isLessonSubmitted}
        lessonList={liveClassLessons}
        onLessonSelected={lessonSelected}
        isSubmitting={isSubmitting}
        onSubmitLesson={handleSubmitLightFeedback}
        showTooltip={!!showRubrics}
        isFreeFormTopic={isFreeFormTopic}
      />

      {isLessonSubmitted && (
        <>
          {showRubrics && (
            <PCFeedbackRubrics
              lessonId={lessonId}
              criteriaPerformanceIds={criteriaPerformanceIds}
              onCriterionPress={(ids: number[]) => {
                setCriteriaPerformanceIds(ids);
              }}
            />
          )}

          {wolverineConfig?.liveClassFeedbackEnabled && (
            <>
              <HorizontalView width={700} style={mt(34)}>
                <HorizontalView width={216}>
                  <OEText fontType="bold">Teacher comments</OEText>
                  <OEText fontType="light">* REQUIRED</OEText>
                </HorizontalView>

                {!isFreeFormTopic && (
                  <HorizontalView width={290}>
                    <Checkbox
                      status={recommendRepeat ? 'checked' : 'unchecked'}
                      onPress={() => setRecommendRepeat(!recommendRepeat)}
                    />
                    <OEText>Recommended to repeat this class</OEText>
                  </HorizontalView>
                )}
              </HorizontalView>

              <CommentTextArea
                multiline
                placeholder="Type here..."
                numberOfLines={5}
                onChangeText={setTeacherFeedbackValue}
                autoComplete="off"
                value={teacherFeedbackValue}
              />
              <ErrorMessageBox text={error} style={mt(24)} />
              {isLessonSubmitted && (
                <OEButton
                  onPress={handleSubmitFeedback}
                  loading={submittingFeedback}
                  disabled={!canSubmit}
                  style={[
                    width(217),
                    height(40),
                    mt(24),
                    flex('align-self-center'),
                  ]}
                  size="huge"
                >
                  Submit feedback
                </OEButton>
              )}
            </>
          )}
        </>
      )}
    </View>
  );
};

interface StyledSizedType {
  width?: number;
}
const HorizontalView = styled.View<StyledSizedType & OEButtonProps>`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-width: ${({ width }) => `${width}px` || '100%'};
  width: ${({ width }) => `${width}px` || '100%'};
  max-width: ${({ width }) => `${width}px` || '100%'};
`;

const CommentTextArea = styled(OETextInput)`
  height: 160px;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  background-color: white;
`;
